
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CurrentAffairUploader } from './CurrentAffairUploader';
import { CurrentAffairViewer } from './CurrentAffairViewer';
import CurrentAffairsAI from './CurrentAffairsAI';
import { CurrentAffairWordUploader } from './CurrentAffairWordUploader';
import { CalendarIcon, Brain, BookOpen, Upload, BarChart4, Sparkles, Loader2, FileText } from 'lucide-react';
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { CurrentAffairCard } from './CurrentAffairCard';
import { useAuthSession } from "@/hooks/useAuthSession";
import CurrentAffairsAdaptiveNotes from './CurrentAffairsAdaptiveNotes';
import { useIsMobile } from '@/hooks/use-mobile';
import { CurrentAffairsFilter } from './CurrentAffairsFilter';

export interface CurrentAffair {
  id: string;
  title: string;
  content: string;
  date?: string;
  time_period?: string;
  language?: string;
  processing_status?: 'pending' | 'processing' | 'processed' | 'error';
  metadata?: {
    enhanced?: boolean;
    questions?: any[];
    [key: string]: any;
  };
}

function CurrentAffairs() {
  const [activeTab, setActiveTab] = useState("viewer");
  const [currentArticle, setCurrentArticle] = useState<CurrentAffair>({
    id: '',
    title: 'Select or upload an article',
    content: '',
    date: '',
    time_period: '',
    language: ''
  });
  const [articles, setArticles] = useState<CurrentAffair[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, userId } = useAuthSession();
  const { isMobile } = useIsMobile();
  
  // Add new filter state
  const [filters, setFilters] = useState({
    searchTerm: '',
    language: 'all',
    timePeriod: 'all',
    date: undefined as Date | undefined
  });

  useEffect(() => {
    fetchCurrentAffairs();
  }, []);

  const fetchCurrentAffairs = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('current_affairs')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (error) {
        console.error("Error fetching current affairs:", error);
        setError("Failed to load current affairs. Please try again.");
        toast.error("Failed to load current affairs");
        return;
      }
      
      console.log("Fetched current affairs:", data);
      
      if (data && data.length > 0) {
        setArticles(data as CurrentAffair[]);
        
        setCurrentArticle(data[0] as CurrentAffair);
      } else {
        setError("No current affairs found. Please upload some content first.");
      }
    } catch (err) {
      console.error("Unexpected error:", err);
      setError("An unexpected error occurred. Please try again later.");
      toast.error("Failed to load current affairs");
    } finally {
      setIsLoading(false);
    }
  };

  const handleArticleSelect = (article: CurrentAffair) => {
    console.log("Selected article:", article);
    setCurrentArticle(article);
  };

  const handleUploadSuccess = () => {
    fetchCurrentAffairs();
    setActiveTab("viewer");
    toast.success("Content uploaded successfully");
  };

  const handleDeleteArticle = async (id: string) => {
    try {
      setIsLoading(true);
      
      if (!isAuthenticated) {
        toast.error("You must be logged in to delete articles");
        return;
      }
      
      console.log("Deleting article with ID:", id);
      
      try {
        const { error } = await supabase
          .from('current_affairs')
          .delete()
          .eq('id', id);
          
        if (error) {
          console.error("Error deleting article:", error);
          toast.error("Failed to delete article: " + error.message);
          return;
        }
        
        console.log("Article successfully deleted");
        
        setArticles(prevArticles => prevArticles.filter(article => article.id !== id));
        
        if (currentArticle.id === id) {
          const remainingArticles = articles.filter(article => article.id !== id);
          if (remainingArticles.length > 0) {
            setCurrentArticle(remainingArticles[0]);
          } else {
            setCurrentArticle({
              id: '',
              title: 'Select or upload an article',
              content: '',
              date: '',
              time_period: '',
              language: ''
            });
          }
        }
        
        toast.success("Article deleted successfully");
      } catch (err) {
        console.error("Unexpected error during deletion:", err);
        toast.error("Failed to delete article");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Filter articles based on search criteria
  const filteredArticles = articles.filter(article => {
    // Filter by search term in title or content
    const matchesSearch = filters.searchTerm === '' || 
      article.title.toLowerCase().includes(filters.searchTerm.toLowerCase()) || 
      article.content.toLowerCase().includes(filters.searchTerm.toLowerCase());
    
    // Filter by language
    const matchesLanguage = filters.language === 'all' || 
      article.language === filters.language;
    
    // Filter by time period
    let matchesTimePeriod = true;
    if (filters.timePeriod !== 'all' && article.time_period) {
      matchesTimePeriod = article.time_period === filters.timePeriod;
    }
    
    // Filter by specific date
    let matchesDate = true;
    if (filters.date && article.date) {
      const articleDate = new Date(article.date).toDateString();
      const filterDate = filters.date.toDateString();
      matchesDate = articleDate === filterDate;
    }
    
    return matchesSearch && matchesLanguage && matchesTimePeriod && matchesDate;
  });

  // Handler to update filters
  const handleFilterChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
  };
  
  // Handler to clear all filters
  const handleClearFilters = () => {
    setFilters({
      searchTerm: '',
      language: 'all',
      timePeriod: 'all',
      date: undefined
    });
  };

  const renderArticleList = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      );
    }
    
    if (error && articles.length === 0) {
      return (
        <div className="p-4 text-center">
          <p className="text-sm text-muted-foreground">{error}</p>
          <Button variant="outline" size="sm" className="mt-2" onClick={fetchCurrentAffairs}>
            Retry
          </Button>
        </div>
      );
    }
    
    if (filteredArticles.length === 0) {
      return (
        <div className="p-4 text-center">
          <p className="text-sm text-muted-foreground">
            {articles.length === 0 ? "No articles found" : "No articles match your search criteria"}
          </p>
          {articles.length > 0 && (
            <Button variant="outline" size="sm" className="mt-2" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          )}
        </div>
      );
    }
    
    return (
      <div className="border rounded-md p-2 max-h-[400px] overflow-y-auto">
        {filteredArticles.map(article => (
          <CurrentAffairCard 
            key={article.id}
            article={article}
            isSelected={currentArticle.id === article.id}
            onSelect={() => handleArticleSelect(article)}
            onDelete={handleDeleteArticle}
            isAdmin={isAuthenticated}
          />
        ))}
      </div>
    );
  };

  return (
    <div className={`container mx-auto ${isMobile ? 'px-1 py-2' : 'p-4'}`}>
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Current Affairs</h1>
          <p className="text-muted-foreground">Stay updated with the latest news and events</p>
        </div>
        
        <div className="flex gap-2 sm:gap-3 flex-wrap justify-end">
          <Link to="/current-affairs/quiz">
            <Button variant="outline" className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-4">
              <BookOpen className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden xs:inline">Regular</span> Quiz
            </Button>
          </Link>
          
          <Link to="/current-affairs/adaptive-quiz">
            <Button className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700">
              <Brain className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden xs:inline">Adaptive</span> Quiz
            </Button>
          </Link>

          <Link to="/current-affairs/analytics">
            <Button variant="outline" className="gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-4">
              <BarChart4 className="h-3 w-3 sm:h-4 sm:w-4" />
              Analytics
            </Button>
          </Link>
        </div>
      </div>
      
      {/* Add filter component */}
      <CurrentAffairsFilter 
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
      />
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-6">
        <div className="md:col-span-1">
          <Card>
            <CardContent className={`${isMobile ? 'p-2 sm:p-4' : 'p-4'}`}>
              <h2 className="text-lg font-medium mb-3">Articles</h2>
              {renderArticleList()}
            </CardContent>
          </Card>
        </div>
        
        <div className="md:col-span-2">
          <Card className="mb-6">
            <CardContent className="p-0">
              <Tabs defaultValue={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="w-full grid grid-cols-5">
                  <TabsTrigger value="viewer" className="flex gap-1 sm:gap-2 text-xs sm:text-sm">
                    <CalendarIcon className="h-3 w-3 sm:h-4 sm:w-4" /> 
                    <span className="hidden xs:inline">View</span> Content
                  </TabsTrigger>
                  <TabsTrigger value="upload" className="flex gap-1 sm:gap-2 text-xs sm:text-sm">
                    <Upload className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden xs:inline">Upload</span> Content
                  </TabsTrigger>
                  <TabsTrigger value="word-upload" className="flex gap-1 sm:gap-2 text-xs sm:text-sm">
                    <Upload className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden xs:inline">Upload</span> Doc
                  </TabsTrigger>
                  <TabsTrigger value="ai" className="flex gap-1 sm:gap-2 text-xs sm:text-sm">
                    <Sparkles className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden xs:inline">AI</span> Tools
                  </TabsTrigger>
                  <TabsTrigger value="adaptive-notes" className="flex gap-1 sm:gap-2 text-xs sm:text-sm">
                    <FileText className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden xs:inline">Adaptive</span> Notes
                  </TabsTrigger>
                </TabsList>
                
                <div className={`${isMobile ? 'p-2' : 'p-4'}`}>
                  <TabsContent value="viewer">
                    <CurrentAffairViewer article={currentArticle} />
                  </TabsContent>
                  <TabsContent value="upload">
                    <CurrentAffairUploader onSuccess={handleUploadSuccess} />
                  </TabsContent>
                  <TabsContent value="word-upload">
                    <CurrentAffairWordUploader onSuccess={handleUploadSuccess} />
                  </TabsContent>
                  <TabsContent value="ai">
                    <CurrentAffairsAI />
                  </TabsContent>
                  <TabsContent value="adaptive-notes">
                    <CurrentAffairsAdaptiveNotes 
                      topic={currentArticle?.title || 'Current Affairs'}
                      date={currentArticle?.date}
                    />
                  </TabsContent>
                </div>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

// Make sure to use a default export
export default CurrentAffairs;
